import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../config/api';

const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  const email = params.get('email')?.trim() || '';
  const token = params.get('token')?.trim() || '';
  return { email, token };
};

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);

  const { email: queryEmail, token: queryToken } = useMemo(() => parseQuery(location.search), [location.search]);

  const [email] = useState(queryEmail);
  const [token] = useState(queryToken);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!email || !token) {
      setIsError(true);
      setMessage('Invalid or expired reset link. Please request a new email.');
    }
  }, [email, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!email || !token) {
      setIsError(true);
      setMessage('Reset link is missing required information.');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setIsError(true);
      setMessage('Please fill out both password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setIsError(true);
      setMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setIsError(true);
      setMessage('Password must be at least 8 characters long.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const response = await apiClient.post('/auth/confirm-reset-password', {
        email: email.toLowerCase(),
        token,
        newPassword,
        confirmPassword,
      });

      const successMessage = response?.data?.message || 'Password updated successfully. Redirecting to login...';
      setIsError(false);
      setMessage(successMessage);

      redirectTimerRef.current = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3500);
    } catch (error) {
      const apiMsg = error?.response?.data?.error;
      setIsError(true);
      setMessage(apiMsg || 'Unable to update password. The link may have expired.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main style={styles.wrapper}>
      <section style={styles.card} aria-labelledby="reset-password-title">
        <h1 id="reset-password-title" style={styles.heading}>Reset your password</h1>
        <p style={styles.helper}>
          Choose a new password for the account associated with <strong>{email || 'your email address'}</strong>.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label htmlFor="new-password" style={styles.label}>New password</label>
            <div style={styles.passwordWrapper}>
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                name="newPassword"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
              <button
                type="button"
                style={styles.toggle}
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div style={styles.field}>
            <label htmlFor="confirm-password" style={styles.label}>Confirm password</label>
            <div style={styles.passwordWrapper}>
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
                style={styles.input}
                disabled={isSubmitting}
              />
              <button
                type="button"
                style={styles.toggle}
                onClick={() => setShowConfirmPassword((value) => !value)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(isSubmitting ? styles.buttonDisabled : {}),
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update password'}
          </button>
        </form>

        {message ? (
          <p
            role="status"
            style={{
              ...styles.message,
              color: isError ? '#b42318' : '#027a48',
            }}
          >
            {message}
          </p>
        ) : null}

        <div style={styles.links}>
          <Link to="/login" style={styles.link}>Back to login</Link>
          <span style={styles.separator}>|</span>
          <Link to="/forgot-password" style={styles.link}>Request another link</Link>
        </div>
      </section>
    </main>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f162b, #1f3c88)',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '36px',
    boxShadow: '0 20px 42px rgba(15, 23, 42, 0.15)',
  },
  heading: {
    margin: '0 0 12px',
    fontSize: '28px',
    fontWeight: 700,
    color: '#1f3c88',
  },
  helper: {
    margin: '0 0 28px',
    color: '#475467',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontWeight: 600,
    color: '#1d2939',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: '1px solid #d0d5dd',
    fontSize: '16px',
  },
  toggle: {
    position: 'absolute',
    right: '14px',
    background: 'none',
    border: 'none',
    color: '#1f3c88',
    fontWeight: 600,
    cursor: 'pointer',
  },
  button: {
    marginTop: '8px',
    background: 'linear-gradient(135deg, #1f3c88, #39a0ed)',
    color: '#fff',
    border: 'none',
    borderRadius: '999px',
    padding: '14px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 14px 24px rgba(31, 60, 136, 0.3)',
  },
  buttonDisabled: {
    opacity: 0.75,
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
  message: {
    marginTop: '24px',
    fontSize: '15px',
    lineHeight: 1.5,
  },
  links: {
    marginTop: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#5f6c7b',
  },
  link: {
    color: '#1f3c88',
    fontWeight: 600,
    textDecoration: 'none',
  },
  separator: {
    color: '#d0d5dd',
  },
};

export default ResetPassword;
